/**
 * Module Registry
 * 
 * Manages all loaded modules and provides a centralized interface
 * for accessing module functionality throughout the application.
 */
import { BaseModule, NavigationItem, RouteItem, ModuleMetadata, DashboardWidget } from './BaseModule';
import { ReactNode } from 'react';

class ModuleRegistry {
    modules: Map<string, BaseModule>;
    initialized: boolean;

    constructor() {
        this.modules = new Map();
        this.initialized = false;
    }

    /**
     * Register a module
     * @param {BaseModule} module - Module instance to register
     */
    register(module: BaseModule): void {
        if (!(module instanceof BaseModule)) {
            throw new Error('Module must extend BaseModule');
        }

        if (this.modules.has(module.id)) {
            // Silently skip if already registered to avoid duplicate warnings
            return;
        }

        this.modules.set(module.id, module);
        console.log(`Registered module: ${module.name} (${module.id})`);
    }

    /**
     * Unregister a module
     * @param {string} moduleId - ID of the module to unregister
     */
    unregister(moduleId: string): void {
        const module = this.modules.get(moduleId);
        if (module) {
            module.cleanup();
            this.modules.delete(moduleId);
            console.log(`Unregistered module: ${module.name} (${moduleId})`);
        }
    }

    /**
     * Get a module by ID
     * @param {string} moduleId - ID of the module to get
     * @returns {BaseModule|null} Module instance or null if not found
     */
    getModule(moduleId: string): BaseModule | null {
        return this.modules.get(moduleId) || null;
    }

    /**
     * Get all registered modules
     * @returns {Array} Array of module instances
     */
    getAllModules(): BaseModule[] {
        return Array.from(this.modules.values());
    }

    /**
     * Get available modules for a user
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Array} Array of available module instances
     */
    getAvailableModules(user: any, userProfile: any): BaseModule[] {
        return this.getAllModules().filter(module =>
            module.isAvailable(user, userProfile)
        );
    }

    /**
     * Get all navigation items from available modules
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Array} Array of navigation items
     */
    getNavigationItems(user: any, userProfile: any): NavigationItem[] {
        const availableModules = this.getAvailableModules(user, userProfile);
        const navigationItems: NavigationItem[] = [];

        availableModules.forEach(module => {
            try {
                const items = module.getNavigationItems(user, userProfile);
                if (Array.isArray(items)) {
                    navigationItems.push(...items);
                }
            } catch (error) {
                console.error(`Error getting navigation items for module ${module.id}:`, error);
            }
        });

        return navigationItems;
    }

    /**
     * Get all routes from available modules
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Array} Array of route objects
     */
    getRoutes(user: any, userProfile: any): RouteItem[] {
        const availableModules = this.getAvailableModules(user, userProfile);
        const routes: RouteItem[] = [];

        availableModules.forEach(module => {
            try {
                const moduleRoutes = module.getRoutes(user, userProfile);
                if (Array.isArray(moduleRoutes)) {
                    routes.push(...moduleRoutes);
                }
            } catch (error) {
                console.error(`Error getting routes for module ${module.id}:`, error);
            }
        });

        return routes;
    }

    /**
     * Get all dashboard widgets from available modules
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Array} Array of widget components
     */
    getDashboardWidgets(user: any, userProfile: any): DashboardWidget[] {
        const availableModules = this.getAvailableModules(user, userProfile);
        const widgets: DashboardWidget[] = [];

        availableModules.forEach(module => {
            try {
                const moduleWidgets = module.getDashboardWidgets(user, userProfile);
                if (Array.isArray(moduleWidgets)) {
                    widgets.push(...moduleWidgets);
                }
            } catch (error) {
                console.error(`Error getting dashboard widgets for module ${module.id}:`, error);
            }
        });

        return widgets;
    }

    /**
     * Get analytics data from all available modules
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Object} Combined analytics data
     */
    async getAnalyticsData(user: any, userProfile: any): Promise<Record<string, any>> {
        const availableModules = this.getAvailableModules(user, userProfile);
        const analyticsData: Record<string, any> = {};

        for (const module of availableModules) {
            try {
                const moduleData = await module.getAnalyticsData(user, userProfile);
                if (moduleData && typeof moduleData === 'object') {
                    analyticsData[module.id] = moduleData;
                }
            } catch (error) {
                console.error(`Error getting analytics data for module ${module.id}:`, error);
            }
        }

        return analyticsData;
    }

    /**
     * Get personal analytics data from all available modules
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Object} Combined personal analytics data
     */
    async getPersonalAnalyticsData(user: any, userProfile: any): Promise<Record<string, any>> {
        const availableModules = this.getAvailableModules(user, userProfile);
        const analyticsData: Record<string, any> = {};

        for (const module of availableModules) {
            try {
                const moduleData = await module.getPersonalAnalyticsData(user, userProfile);
                if (moduleData && typeof moduleData === 'object') {
                    analyticsData[module.id] = moduleData;
                }
            } catch (error) {
                console.error(`Error getting personal analytics data for module ${module.id}:`, error);
            }
        }

        return analyticsData;
    }

    /**
     * Get help content from all available modules
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Object} Combined help content
     */
    getHelpContent(user: any, userProfile: any): Record<string, any> {
        const availableModules = this.getAvailableModules(user, userProfile);
        const helpContent: Record<string, any> = {};

        availableModules.forEach(module => {
            try {
                const moduleHelp = module.getHelpContent(user, userProfile);
                if (moduleHelp && typeof moduleHelp === 'object') {
                    helpContent[module.id] = moduleHelp;
                }
            } catch (error) {
                console.error(`Error getting help content for module ${module.id}:`, error);
            }
        });

        return helpContent;
    }

    /**
     * Get feedback triggers from all available modules
     * @param {Object} user - Current user object
     * @param {Object} userProfile - Current user profile
     * @returns {Array} Array of feedback trigger functions
     */
    getFeedbackTriggers(user: any, userProfile: any): any[] {
        const availableModules = this.getAvailableModules(user, userProfile);
        const triggers: any[] = [];

        availableModules.forEach(module => {
            try {
                const moduleTriggers = module.getFeedbackTriggers();
                if (Array.isArray(moduleTriggers)) {
                    triggers.push(...moduleTriggers);
                }
            } catch (error) {
                console.error(`Error getting feedback triggers for module ${module.id}:`, error);
            }
        });

        return triggers;
    }

    /**
     * Get all Firestore collections used by modules
     * @returns {Array} Array of collection names
     */
    getCollections(): string[] {
        const collections = new Set<string>();

        this.getAllModules().forEach(module => {
            try {
                const moduleCollections = module.getCollections();
                if (Array.isArray(moduleCollections)) {
                    moduleCollections.forEach(collection => collections.add(collection));
                }
            } catch (error) {
                console.error(`Error getting collections for module ${module.id}:`, error);
            }
        });

        return Array.from(collections);
    }

    /**
     * Get Firestore security rules for all modules
     * @returns {string} Combined security rules
     */
    getSecurityRules(): string {
        const rules: string[] = [];

        this.getAllModules().forEach(module => {
            try {
                const moduleRules = module.getSecurityRules();
                if (moduleRules && typeof moduleRules === 'string') {
                    rules.push(moduleRules);
                }
            } catch (error) {
                console.error(`Error getting security rules for module ${module.id}:`, error);
            }
        });

        return rules.join('\n\n');
    }

    /**
     * Initialize all modules
     */
    async initialize(): Promise<void> {
        if (this.initialized) {
            return;
        }

        console.log('Initializing module registry...');

        for (const module of this.getAllModules()) {
            try {
                await module.initialize();
            } catch (error) {
                console.error(`Error initializing module ${module.id}:`, error);
            }
        }

        this.initialized = true;
        console.log('Module registry initialized');
    }

    /**
     * Get module metadata for all modules
     * @returns {Array} Array of module metadata
     */
    getModulesMetadata(): ModuleMetadata[] {
        return this.getAllModules().map(module => module.getMetadata());
    }
}

// Create singleton instance
const moduleRegistry = new ModuleRegistry();

export default moduleRegistry;
